/**
 * PATH: apps/qa/src/pages/new-inspection/FloorsStep.tsx
 * PURPOSE: Step 2 — floors & areas builder with templates, bulk add, file uploads
 */

import { useState } from 'react'
import { Building2, Plus, Trash2, Layers, LayoutGrid, ListPlus, ChevronRight, Upload, Camera, FileText, Paperclip, Tag, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { FLOOR_TEMPLATES, AREA_TEMPLATES } from '@/lib/inspection-constants'
import { JobContextCard, FloorBuilder, type FloorConfig } from '@/components/inspection'
import type { Job, FloorDraft, ToastNotification } from './inspection-types'

interface FloorsStepProps {
  selectedJob: Job | null
  buildingClasses: string[]; tradeTags: string[]
  category: string; projectType: string
  floors: FloorDraft[]; totalAreas: number
  addFloor: (template?: any) => void; removeFloor: (id: string) => void
  updateFloor: (id: string, name: string) => void; updateFloorNotes: (id: string, notes: string) => void
  addArea: (floorId: string, name?: string) => void; addBulkAreas: (floorId: string, text: string) => void
  removeArea: (floorId: string, areaId: string) => void
  updateArea: (floorId: string, areaId: string, updates: any) => void
  toggleAreaExpanded: (floorId: string, areaId: string) => void
  addAreaAttachments: (floorId: string, areaId: string, files: FileList | null) => void
  removeAreaAttachment: (floorId: string, areaId: string, attId: string) => void
  applyGeneratedFloors: (floors: any[]) => void
  showToast: (toast: Omit<ToastNotification, 'id'>) => void
}

export default function FloorsStep(props: FloorsStepProps) {
  const {
    selectedJob, buildingClasses, tradeTags, category, projectType,
    floors, totalAreas, addFloor, removeFloor, updateFloor, updateFloorNotes,
    addArea, addBulkAreas, removeArea, updateArea, toggleAreaExpanded,
    addAreaAttachments, removeAreaAttachment, applyGeneratedFloors, showToast,
  } = props

  const [showStructureBuilder, setShowStructureBuilder] = useState(false)
  const [showFloorTemplates, setShowFloorTemplates] = useState(false)
  const [showBulkAdd, setShowBulkAdd] = useState<string | null>(null)
  const [bulkAreaText, setBulkAreaText] = useState('')

  const handleGenerate = (generatedFloors: FloorConfig[]) => {
    if (floors.length > 0 && !window.confirm(`This will replace your ${floors.length} existing floor(s). Continue?`)) return
    applyGeneratedFloors(generatedFloors)
    setShowStructureBuilder(false)
    showToast({ type: 'success', message: `Generated ${generatedFloors.length} floors with ${generatedFloors.reduce((sum, f) => sum + f.areas.length, 0)} areas` })
  }

  const handleBulkAdd = (floorId: string) => {
    addBulkAreas(floorId, bulkAreaText)
    setShowBulkAdd(null)
    setBulkAreaText('')
  }

  return (
    <div className="space-y-6">
      <JobContextCard job={selectedJob} compact />

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground mb-1">Floors & Areas</h2>
          <p className="text-muted-foreground text-sm">Define the structure of your inspection. You can add more later.</p>
        </div>
        <button onClick={() => setShowStructureBuilder(!showStructureBuilder)}
          className={cn('inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors',
            showStructureBuilder ? 'border-primary bg-primary/10 text-primary' : 'border-input hover:bg-accent')}>
          <Building2 className="w-4 h-4" />Quick Builder
        </button>
      </div>

      {showStructureBuilder && (
        <FloorBuilder job={selectedJob} orgPrimaryTrade={null} buildingClasses={buildingClasses}
          tradeTags={tradeTags} category={category} projectType={projectType}
          onGenerate={handleGenerate} onCancel={() => setShowStructureBuilder(false)} />
      )}

      {/* Quick Floor Templates */}
      <div className="flex flex-wrap gap-2">
        <span className="text-sm text-muted-foreground py-1.5">Quick add:</span>
        {FLOOR_TEMPLATES.slice(0, 4).map(template => (
          <button key={template.name} onClick={() => addFloor(template)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground hover:bg-accent hover:border-primary/50 transition-all">
            <Plus className="w-3.5 h-3.5" />{template.name}
          </button>
        ))}
        <button onClick={() => setShowFloorTemplates(!showFloorTemplates)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dashed border-border text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-all">
          <LayoutGrid className="w-3.5 h-3.5" />More...
        </button>
      </div>

      {showFloorTemplates && (
        <div className="p-4 rounded-lg border border-border bg-muted/30 animate-in slide-in-from-top-2">
          <p className="text-sm text-muted-foreground mb-3">All floor templates (with suggested areas)</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {FLOOR_TEMPLATES.map(template => (
              <button key={template.name} onClick={() => { addFloor(template); setShowFloorTemplates(false) }}
                className="p-3 rounded-lg border border-border hover:border-primary hover:bg-primary/5 text-left transition-all">
                <p className="font-medium text-sm text-foreground">{template.name}</p>
                <p className="text-xs text-muted-foreground truncate">{template.areas.join(', ')}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Custom Floor Button */}
      <button onClick={() => addFloor()}
        className="w-full py-3 rounded-xl border-2 border-dashed border-primary/30 text-primary hover:border-primary hover:bg-primary/5 transition-all inline-flex items-center justify-center gap-2">
        <Plus className="w-4 h-4" />Add Custom Floor
      </button>

      {/* Floors List */}
      {floors.length > 0 && (
        <div className="space-y-4">
          {floors.map((floor, floorIdx) => (
            <FloorCard key={floor.id} floor={floor} floorIdx={floorIdx} tradeTags={tradeTags}
              updateFloor={updateFloor} updateFloorNotes={updateFloorNotes} removeFloor={removeFloor}
              addArea={addArea} removeArea={removeArea} updateArea={updateArea}
              toggleAreaExpanded={toggleAreaExpanded} addAreaAttachments={addAreaAttachments}
              removeAreaAttachment={removeAreaAttachment}
              showBulkAdd={showBulkAdd} setShowBulkAdd={setShowBulkAdd}
              bulkAreaText={bulkAreaText} setBulkAreaText={setBulkAreaText}
              onBulkAdd={handleBulkAdd} />
          ))}
        </div>
      )}

      {/* Empty State */}
      {floors.length === 0 && (
        <div className="text-center py-8 border-2 border-dashed border-border rounded-xl bg-muted/30">
          <Layers className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
          <p className="text-foreground font-medium">No floors added yet</p>
          <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">Use the quick add buttons above or create custom floors. You can also skip this step.</p>
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────

function FloorCard({ floor, floorIdx, tradeTags, updateFloor, updateFloorNotes, removeFloor, addArea, removeArea, updateArea, toggleAreaExpanded, addAreaAttachments, removeAreaAttachment, showBulkAdd, setShowBulkAdd, bulkAreaText, setBulkAreaText, onBulkAdd }: {
  floor: FloorDraft; floorIdx: number; tradeTags: string[]
  updateFloor: (id: string, name: string) => void; updateFloorNotes: (id: string, notes: string) => void; removeFloor: (id: string) => void
  addArea: (floorId: string) => void; removeArea: (fId: string, aId: string) => void
  updateArea: (fId: string, aId: string, u: any) => void; toggleAreaExpanded: (fId: string, aId: string) => void
  addAreaAttachments: (fId: string, aId: string, files: FileList | null) => void; removeAreaAttachment: (fId: string, aId: string, attId: string) => void
  showBulkAdd: string | null; setShowBulkAdd: (v: string | null) => void
  bulkAreaText: string; setBulkAreaText: (v: string) => void; onBulkAdd: (floorId: string) => void
}) {
  return (
    <div className="border border-border rounded-xl overflow-hidden bg-card animate-in slide-in-from-top-2 duration-200">
      <div className="flex items-center gap-3 p-4 bg-muted/50 border-b border-border">
        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary font-medium text-sm">{floorIdx + 1}</div>
        <input type="text" value={floor.floor_name} onChange={(e) => updateFloor(floor.id, e.target.value)}
          className="flex-1 bg-transparent font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 px-2 py-1 rounded transition-all" placeholder="Floor name" />
        <span className="text-sm text-muted-foreground bg-background px-2 py-0.5 rounded">{floor.areas.length} area{floor.areas.length !== 1 ? 's' : ''}</span>
        <button onClick={() => removeFloor(floor.id)} className="p-1.5 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors" aria-label="Remove floor"><Trash2 className="w-4 h-4" /></button>
      </div>
      <div className="px-4 pt-2">
        <textarea value={floor.notes} onChange={(e) => updateFloorNotes(floor.id, e.target.value)} placeholder="Add floor-level notes (optional)..." rows={1}
          className="w-full text-xs p-1.5 rounded border border-transparent bg-transparent text-muted-foreground placeholder:text-muted-foreground/50 focus:border-input focus:bg-background focus:text-foreground focus:outline-none focus:ring-1 focus:ring-primary/20 resize-none transition-all" />
      </div>
      <div className="p-4 space-y-3">
        {floor.areas.map((area) => (
          <AreaRow key={area.id} area={area} floorId={floor.id} tradeTags={tradeTags}
            updateArea={updateArea} removeArea={removeArea} toggleAreaExpanded={toggleAreaExpanded}
            addAreaAttachments={addAreaAttachments} removeAreaAttachment={removeAreaAttachment} />
        ))}
        <div className="flex flex-wrap gap-2 pt-2">
          <button onClick={() => addArea(floor.id)}
            className="flex-1 py-2 rounded-lg border border-dashed border-border text-sm text-muted-foreground hover:text-primary hover:border-primary/50 hover:bg-primary/5 transition-all inline-flex items-center justify-center gap-1.5">
            <Plus className="w-3.5 h-3.5" />Add Area
          </button>
          <button onClick={() => setShowBulkAdd(showBulkAdd === floor.id ? null : floor.id)}
            className={cn('px-3 py-2 rounded-lg border text-sm transition-all inline-flex items-center gap-1.5',
              showBulkAdd === floor.id ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:text-foreground hover:bg-accent')}>
            <ListPlus className="w-3.5 h-3.5" />Bulk Add
          </button>
        </div>
        {showBulkAdd === floor.id && (
          <BulkAddPanel bulkAreaText={bulkAreaText} setBulkAreaText={setBulkAreaText}
            onAdd={() => onBulkAdd(floor.id)} onCancel={() => { setShowBulkAdd(null); setBulkAreaText('') }} />
        )}
      </div>
    </div>
  )
}

function AreaRow({ area, floorId, tradeTags, updateArea, removeArea, toggleAreaExpanded, addAreaAttachments, removeAreaAttachment }: {
  area: FloorDraft['areas'][0]; floorId: string; tradeTags: string[]
  updateArea: (fId: string, aId: string, u: any) => void; removeArea: (fId: string, aId: string) => void
  toggleAreaExpanded: (fId: string, aId: string) => void
  addAreaAttachments: (fId: string, aId: string, files: FileList | null) => void; removeAreaAttachment: (fId: string, aId: string, attId: string) => void
}) {
  return (
    <div className={cn('rounded-lg border transition-all animate-in fade-in duration-150', area.isExpanded ? 'bg-background border-primary/40 shadow-sm' : 'bg-muted/30 border-border hover:border-primary/30 group')}>
      <div className="flex items-center gap-3 p-3">
        <button onClick={() => toggleAreaExpanded(floorId, area.id)} className="w-6 h-6 rounded bg-muted flex items-center justify-center text-xs text-muted-foreground hover:bg-primary/10 hover:text-primary transition-colors" title={area.isExpanded ? 'Collapse details' : 'Expand to add files, notes, trade'}>
          <ChevronRight className={cn('w-3.5 h-3.5 transition-transform', area.isExpanded && 'rotate-90')} />
        </button>
        <input type="text" value={area.area_name} onChange={(e) => updateArea(floorId, area.id, { area_name: e.target.value })} placeholder="Area name" className="flex-1 bg-transparent text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 px-2 py-1 rounded transition-all" />
        <input type="text" value={area.area_code} onChange={(e) => updateArea(floorId, area.id, { area_code: e.target.value })} placeholder="Code" className="w-24 bg-background text-foreground text-xs font-mono focus:outline-none focus:ring-2 focus:ring-primary/20 px-2 py-1 rounded border border-input transition-all" />
        {(area.attachments.length > 0 || area.notes.trim() || area.trade) && !area.isExpanded && (
          <div className="flex items-center gap-1">
            {area.attachments.length > 0 && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"><Paperclip className="w-2.5 h-2.5" />{area.attachments.length}</span>}
            {area.trade && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300"><Tag className="w-2.5 h-2.5" /></span>}
          </div>
        )}
        <button onClick={() => removeArea(floorId, area.id)} className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors opacity-0 group-hover:opacity-100" aria-label="Remove area"><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
      {area.isExpanded && (
        <div className="px-3 pb-3 space-y-3 border-t border-border/50 pt-3 ml-9 mr-3 animate-in slide-in-from-top-1 duration-200">
          {tradeTags.length > 0 && (
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1"><Tag className="w-3 h-3" />Trade / Category</label>
              <div className="flex flex-wrap gap-1.5">
                {tradeTags.map(tag => (
                  <button key={tag} onClick={() => updateArea(floorId, area.id, { trade: area.trade === tag ? '' : tag })}
                    className={cn('px-2 py-0.5 rounded-full text-xs font-medium transition-all', area.trade === tag ? 'bg-teal-600 text-white' : 'bg-muted text-muted-foreground hover:bg-accent hover:text-foreground')}>{tag}</button>
                ))}
              </div>
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1"><FileText className="w-3 h-3" />Notes / Instructions</label>
            <textarea value={area.notes} onChange={(e) => updateArea(floorId, area.id, { notes: e.target.value })} placeholder="Scope description, instructions for inspector, reference info..." rows={2}
              className="w-full text-xs p-2 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 resize-y transition-all" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1"><Paperclip className="w-3 h-3" />Attachments <span className="text-muted-foreground/60 font-normal">(drawings, photos, specs)</span></label>
            {area.attachments.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {area.attachments.map(att => (
                  <div key={att.id} className="relative group/att flex items-center gap-1.5 px-2 py-1 rounded-lg border border-border bg-muted/50 text-xs">
                    {att.preview ? <img src={att.preview} alt={att.name} className="w-8 h-8 rounded object-cover flex-shrink-0" /> : <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
                    <span className="truncate max-w-[100px] text-foreground">{att.name}</span>
                    <button onClick={() => removeAreaAttachment(floorId, area.id, att.id)} className="p-0.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors" aria-label={`Remove ${att.name}`}><X className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <label className="flex-1 cursor-pointer"><input type="file" multiple accept="image/*" className="hidden" onChange={(e) => { addAreaAttachments(floorId, area.id, e.target.files); e.target.value = '' }} />
                <div className="flex items-center justify-center gap-1.5 py-2 rounded-lg border border-dashed border-border text-xs text-muted-foreground hover:text-primary hover:border-primary/50 hover:bg-primary/5 transition-all"><Camera className="w-3.5 h-3.5" />Photos</div>
              </label>
              <label className="flex-1 cursor-pointer"><input type="file" multiple accept="application/pdf,.doc,.docx,.xls,.xlsx,.dwg,.dxf,.txt" className="hidden" onChange={(e) => { addAreaAttachments(floorId, area.id, e.target.files); e.target.value = '' }} />
                <div className="flex items-center justify-center gap-1.5 py-2 rounded-lg border border-dashed border-border text-xs text-muted-foreground hover:text-amber-600 hover:border-amber-500/50 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-all"><Upload className="w-3.5 h-3.5" />Documents</div>
              </label>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function BulkAddPanel({ bulkAreaText, setBulkAreaText, onAdd, onCancel }: {
  bulkAreaText: string; setBulkAreaText: (v: string) => void; onAdd: () => void; onCancel: () => void
}) {
  return (
    <div className="p-3 rounded-lg border border-primary/30 bg-primary/5 animate-in slide-in-from-top-2">
      <p className="text-xs text-muted-foreground mb-2">Enter area names (one per line, or comma-separated)</p>
      <textarea value={bulkAreaText} onChange={(e) => setBulkAreaText(e.target.value)} placeholder={"Living Room\nKitchen\nBedroom 1\nBedroom 2"} className="w-full h-24 px-3 py-2 rounded-lg border border-input bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none" />
      <div className="flex gap-2 mt-2">
        <button onClick={onAdd} disabled={!bulkAreaText.trim()} className="flex-1 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">Add Areas</button>
        <button onClick={onCancel} className="px-3 py-1.5 rounded-lg border border-input text-sm hover:bg-accent transition-colors">Cancel</button>
      </div>
      <div className="mt-3 pt-3 border-t border-border">
        <p className="text-xs text-muted-foreground mb-2">Quick templates:</p>
        <div className="flex flex-wrap gap-1">
          {Object.entries(AREA_TEMPLATES).map(([key, areas]) => (
            <button key={key} onClick={() => setBulkAreaText(areas.join('\n'))} className="text-xs px-2 py-1 rounded bg-muted text-muted-foreground hover:text-foreground hover:bg-accent transition-colors capitalize">{key.replace('_', ' ')}</button>
          ))}
        </div>
      </div>
    </div>
  )
}
